import PropTypes from 'prop-types';
import {
	Paper,
	Box,
	Typography,
	FormControl,
	Select,
	MenuItem,
	Tooltip,
	CircularProgress,
	Alert,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const GmailLabelDropdown = ({
	labels,
	selectedLabel,
	onLabelChange,
	isLoading,
	error,
	disabled,
	emailDirection,
}) => {
	const getDefaultLabel = () => {
		switch (emailDirection) {
			case 'received':
				return 'Inbox (default)';
			case 'sent':
				return 'Sent (default)';
			case 'received_and_sent':
				return 'Inbox & Sent (default)';
			default:
				return 'All emails (no label filter)';
		}
	};

	return (
		<Paper
			variant='outlined'
			sx={{
				px: 3,
				py: 2,
				mb: 2,
				borderRadius: 0.8,
				backgroundColor: '#f9fbfc',
			}}
		>
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Typography
						variant='subtitle1'
						sx={{ fontWeight: 600, mb: 0.1, fontSize: '0.9rem' }}
					>
						Gmail Label Selection
					</Typography>

					<Tooltip
						title='Select a specific label to sync emails from. Only emails in the selected label will be synced to your CRM.'
						placement='top'
						arrow
						slotProps={{
							tooltip: {
								sx: {
									borderRadius: '5px',
								},
							},
						}}
					>
						<InfoOutlined
							sx={{
								fontSize: '1rem',
								color: 'text.secondary',
								cursor: 'help',
								mb: 0.3,
							}}
						/>
					</Tooltip>
				</Box>
				<Typography
					variant='body2'
					color='text.secondary'
					sx={{ fontSize: '0.8rem' }}
				>
					Choose which Gmail label to sync (optional)
				</Typography>
			</Box>

			{error && (
				<Alert severity='error' sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<FormControl
				fullWidth
				sx={{ backgroundColor: 'white' }}
				disabled={disabled || isLoading}
			>
				<Select
					value={selectedLabel || ''}
					onChange={(e) => onLabelChange(e.target.value)}
					displayEmpty
					endAdornment={
						isLoading && <CircularProgress color='inherit' size={20} />
					}
					renderValue={(value) => {
						if (value === '') {
							return getDefaultLabel();
						}
						const selectedLabelObj = labels.find((l) => l.id === value);
						return selectedLabelObj?.name || '';
					}}
				>
					<MenuItem value=''>{getDefaultLabel()}</MenuItem>
					{labels.map((label) => (
						<MenuItem key={label.id} value={label.id}>
							{label.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Paper>
	);
};

GmailLabelDropdown.propTypes = {
	labels: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
		})
	),
	selectedLabel: PropTypes.string,
	onLabelChange: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
	error: PropTypes.string,
	disabled: PropTypes.bool,
	emailDirection: PropTypes.oneOf(['received', 'sent', 'received_and_sent']),
};

GmailLabelDropdown.defaultProps = {
	labels: [],
	selectedLabel: '',
	isLoading: false,
	error: null,
	disabled: false,
	emailDirection: 'received_and_sent',
};

export default GmailLabelDropdown;
